import React, { useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@mui/material';
import { License, revokeLicense } from '../../services/onpremApi';

interface Props {
  license: License | null;
  open: boolean;
  onClose: () => void;
  onRevoked: (license: License) => void;
}

const RevokeLicenseDialog: React.FC<Props> = ({ license, open, onClose, onRevoked }) => {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    setReason('');
    setError(null);
    onClose();
  };

  const handleConfirm = async () => {
    if (!license || !reason.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const updated = await revokeLicense(license.id, reason.trim());
      onRevoked(updated);
      handleClose();
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? 'No se pudo revocar la licencia');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Revocar licencia</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <DialogContentText sx={{ mb: 2 }}>
          {license?.organization_name} · <code>{license?.license_key}</code>
          <br />
          La instalación asociada dejará de renovar su licencia y entrará en modo
          restringido cuando venza el JWT actual. Esta acción no se puede deshacer.
        </DialogContentText>
        <TextField
          label="Motivo"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          fullWidth
          required
          autoFocus
          multiline
          minRows={2}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancelar</Button>
        <Button
          onClick={handleConfirm}
          color="error"
          variant="contained"
          disabled={!reason.trim() || submitting}
        >
          Revocar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RevokeLicenseDialog;
